import React, { useRef } from "react";
import {
  Box,
  List,
  Typography,
  Divider,
  Skeleton,
  useTheme,
} from "@mui/material";
import { AddCommentOutlined as AddCommentOutlinedIcon } from "@mui/icons-material";
import { ChatThread } from "@/store/chatSlice";
import { CustomThreadItem } from "../CustomThreadItem";
import { CustomButton, colors } from "@ippon/shared-components";
import { isScrolledToBottom } from "./scrollPosition";

interface CustomThreadSidebarProps {
  threads: ChatThread[];
  selectedThreadId?: string;
  onThreadSelect: (threadId: string) => void;
  /** 指定時、履歴の上に「新規チャット」ボタンを表示する。 */
  onNewChat?: () => void;
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  width?: number;
  padding?: string;
  useTruncateText?: boolean;
  /** プレビュー文字数の上限（幅の広い場所では長めに）。 */
  previewMaxLength?: number | null;
  /** true のとき、本文プレビューより title(全文)を優先表示する。 */
  preferTitle?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  bottomObserverRef?: React.RefObject<HTMLDivElement>;
  bottomObserverDataAttr?: string;
  onThreadSelectCallback?: () => void;
}

// 履歴読み込み中のスケルトン行（スレッド項目の見た目に合わせる）。
const ThreadSkeletonList: React.FC<{ padding: string; rows?: number }> = ({
  padding,
  rows = 6,
}) => (
  <Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Box key={i} sx={{ padding, minHeight: "72px", boxSizing: "border-box" }}>
        <Skeleton variant="text" width="82%" height={20} sx={{ mb: "4px" }} />
        <Skeleton variant="text" width="38%" height={16} />
      </Box>
    ))}
  </Box>
);

const CustomThreadSidebar: React.FC<CustomThreadSidebarProps> = ({
  threads,
  selectedThreadId,
  onThreadSelect,
  onNewChat,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  width = 280,
  padding = "12px 20px",
  useTruncateText = true,
  previewMaxLength,
  preferTitle,
  scrollContainerRef: externalScrollContainerRef,
  bottomObserverRef: externalBottomObserverRef,
  bottomObserverDataAttr,
  onThreadSelectCallback,
}) => {
  const theme = useTheme();
  const internalScrollContainerRef = useRef<HTMLDivElement>(null);
  const internalBottomObserverRef = useRef<HTMLDivElement>(null);
  
  const scrollContainerRef = externalScrollContainerRef || internalScrollContainerRef;
  const bottomObserverRef = externalBottomObserverRef || internalBottomObserverRef;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || isLoading) return;
    if (isScrolledToBottom(event.currentTarget)) {
      onLoadMore();
    }
  };

  return (
    <Box
      sx={{
        width: width ? `${width}px` : "100%",
        height: "100%",
        backgroundColor: "#FFFFFF",
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* 新規チャット（履歴の上に配置）。塗りの submit ボタン、文字は中央。 */}
      {onNewChat && (
        <Box sx={{ p: 1.5, flexShrink: 0 }}>
          <CustomButton
            label="新規チャット"
            buttonCategory="submit"
            fullWidth
            startIcon={<AddCommentOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={onNewChat}
            sx={{ color: colors.text.inverse }}
          />
        </Box>
      )}

      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehaviorY: "contain",
        }}
      >
        {isLoading && threads.length === 0 ? (
          <ThreadSkeletonList padding={padding} />
        ) : !isLoading && threads.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 3,
              px: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "#9E9E9E",
                textAlign: "center",
              }}
            >
              会話履歴はありません
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {threads
              .filter((thread, index, self) => 
                index === self.findIndex(t => t.thread_id === thread.thread_id)
              )
              .map((thread) => {
                const isSelected = selectedThreadId === thread.thread_id;
                const handleClick = () => {
                  onThreadSelect(thread.thread_id);
                  if (onThreadSelectCallback) {
                    onThreadSelectCallback();
                  }
                };
                return (
                  <React.Fragment key={thread.thread_id}>
                    <CustomThreadItem
                      thread={thread}
                      isSelected={isSelected}
                      onClick={handleClick}
                      padding={padding}
                      useTruncateText={useTruncateText}
                      previewMaxLength={previewMaxLength}
                      preferTitle={preferTitle}
                    />
                    <Divider sx={{ borderColor: theme.palette.divider }} />
                  </React.Fragment>
                );
              })}
          </List>
        )}
        {hasMore && (
          <Box
            ref={bottomObserverRef}
            {...(bottomObserverDataAttr ? { [bottomObserverDataAttr]: true } : {})}
          >
            {/* 追加読み込み中はスケルトン行を出して、切り替わりを滑らかに見せる。 */}
            {isLoading ? (
              <ThreadSkeletonList padding={padding} rows={2} />
            ) : (
              <Box sx={{ height: "20px" }} />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomThreadSidebar;
